import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { TargetCostData } from '@/types/target-cost';
import { formatValue } from '@/utils/formatters';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HelpCircle } from 'lucide-react';

interface CostsFormProps {
  costs: TargetCostData['costs'];
  onChange: (updates: Partial<TargetCostData['costs']>) => void;
}

export function CostsForm({ costs, onChange }: CostsFormProps) {
  const { totalPrices, finalPWDD, costOverrun, savings } = costs;

  // Local state for the text values of the inputs.
  const [totalPricesText, setTotalPricesText] = useState<string>('');
  const [finalPWDDText, setFinalPWDDText] = useState<string>('');

  // When the parent's state changes, update the local text fields.
  useEffect(() => {
    setTotalPricesText(
      totalPrices != null ? Number(totalPrices).toLocaleString('en-US') : ''
    );
  }, [totalPrices]);

  useEffect(() => {
    setFinalPWDDText(
      finalPWDD != null ? Number(finalPWDD).toLocaleString('en-US') : ''
    );
  }, [finalPWDD]);

  /**
   * Derived calculation for Cost Overrun.
   * If finalPWDD > totalPrices, it equals finalPWDD - totalPrices, otherwise 0.
   */
  const computedCostOverrun = useMemo(() => {
    if (totalPrices == null || finalPWDD == null) return null;
    return finalPWDD > totalPrices ? finalPWDD - totalPrices : 0;
  }, [totalPrices, finalPWDD]);

  /**
   * Derived calculation for Savings.
   * If totalPrices > finalPWDD, it equals totalPrices - finalPWDD, otherwise 0.
   */
  const computedSavings = useMemo(() => {
    if (totalPrices == null || finalPWDD == null) return null;
    return totalPrices > finalPWDD ? totalPrices - finalPWDD : 0;
  }, [totalPrices, finalPWDD]);

  /**
   * When totalPrices or finalPWDD changes, update the parent's state
   * with the computed derived values.
   */
  useEffect(() => {
    if (totalPrices != null && finalPWDD != null) {
      if (costOverrun !== computedCostOverrun || savings !== computedSavings) {
        onChange({
          costOverrun: computedCostOverrun,
          savings: computedSavings,
        });
      }
    }
  }, [
    totalPrices,
    finalPWDD,
    computedCostOverrun,
    computedSavings,
    costOverrun,
    savings,
    onChange
  ]);

  /**
   * Handles changes to the totalPrices field.
   * It removes commas from the input, updates the local text state,
   * and propagates the raw numeric value to the parent state.
   */
  const handleTotalPricesChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      // Remove commas so the raw number can be parsed.
      const sanitized = input.replace(/,/g, '');
      setTotalPricesText(sanitized);
      const parsed = sanitized !== '' ? parseFloat(sanitized) : null;
      onChange({ totalPrices: parsed });
    },
    [onChange]
  );

  /**
   * On blur, reformat the totalPrices text with commas.
   */
  const handleTotalPricesBlur = useCallback(() => {
    if (totalPrices != null) {
      setTotalPricesText(Number(totalPrices).toLocaleString('en-US'));
    }
  }, [totalPrices]);

  /**
   * Handles changes to the finalPWDD field.
   * It removes commas, updates the local text state,
   * and propagates the raw numeric value to the parent.
   */
  const handleFinalPWDDChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      const sanitized = input.replace(/,/g, '');
      setFinalPWDDText(sanitized);
      const parsed = sanitized !== '' ? parseFloat(sanitized) : null;
      onChange({ finalPWDD: parsed });
    },
    [onChange]
  );

  /**
   * On blur, reformat the finalPWDD text with commas.
   */
  const handleFinalPWDDBlur = useCallback(() => {
    if (finalPWDD != null) {
      setFinalPWDDText(Number(finalPWDD).toLocaleString('en-US'));
    }
  }, [finalPWDD]);

  /**
   * Renders a tooltip icon with the provided content.
   */
  const renderTooltip = useCallback(
    (content: string) => (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
          </TooltipTrigger>
          <TooltipContent>
            <p className="max-w-xs">{content}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ),
    []
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Total Prices Field */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="totalPrices">The Total of the Prices (£)</Label>
            {renderTooltip("Enter the total agreed contract price for the project.")}
          </div>
          <Input
            id="totalPrices"
            type="text"
            value={totalPricesText}
            onChange={handleTotalPricesChange}
            onBlur={handleTotalPricesBlur}
            placeholder="Enter total prices"
            className="text-right"
            inputMode="numeric"
          />
        </div>

        {/* Final PWDD Field */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="finalPWDD">Final PWDD (£)</Label>
            {renderTooltip("Enter the final Price for Work Done to Date, including Defined Cost and Fee.")}
          </div>
          <Input
            id="finalPWDD"
            type="text"
            value={finalPWDDText}
            onChange={handleFinalPWDDChange}
            onBlur={handleFinalPWDDBlur}
            placeholder="Enter final PWDD"
            className="text-right"
            inputMode="numeric"
          />
        </div>
      </div>
    </div>
  );
}